import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, Download, Loader2, Play, Trash2, Wrench, XCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errText } from "@/lib/api";
import { fmtDateTime, fmtDuration, fmtRelative } from "@/lib/time";
import { SeeMore, useSeeMore } from "./SeeMore";

const STATUS = {
  ok: { label: "OK", cls: "bg-[#16A34A] text-white", Icon: CheckCircle2 },
  fixed: { label: "Fixed", cls: "bg-[#2563EB] text-white", Icon: Wrench },
  warning: { label: "Needs a look", cls: "bg-[#D97706] text-white", Icon: AlertTriangle },
  failed: { label: "Failed", cls: "bg-[#E62314] text-white", Icon: XCircle },
  running: { label: "Running", cls: "bg-[#0A0A0A] text-white", Icon: Loader2 },
};
const Badge = ({ s, testId }) => {
  const m = STATUS[s] || STATUS.ok;
  return <span data-testid={testId} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${m.cls}`}><m.Icon className={`h-3 w-3 ${s === "running" ? "animate-spin" : ""}`} />{m.label}</span>;
};
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

const RunRow = ({ run, open, onToggle, onDelete }) => {
  const jobs = Array.isArray(run.jobs) ? run.jobs : [];
  const took = run.finished_at ? fmtDuration(new Date(run.finished_at) - new Date(run.started_at)) : null;
  return (
    <li className="border-b border-black/5" data-testid={`run-${run.id}`}>
      <div className="flex items-center">
      <button onClick={onToggle} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 py-3.5 pl-5 pr-2 text-left hover:bg-black/[0.02]" data-testid={`run-${run.id}-toggle`}>
        <Badge s={run.status} testId={`run-${run.id}-status`} />
        <span className="min-w-0 flex-1 break-words text-sm font-semibold">{run.title}{run.trigger === "manual" ? "" : " · scheduled"}</span>
        <span className="text-xs text-[#57534E]">{fmtDateTime(run.started_at)}{took ? ` · took ${took}` : ""}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {run.status !== "running" && (
        <button onClick={onDelete} aria-label="Delete report" data-testid={`run-${run.id}-delete`} className="mr-3 rounded p-1.5 text-[#57534E] hover:bg-black/5 hover:text-[#E62314]"><Trash2 className="h-4 w-4" /></button>
      )}
      </div>
      {open && (
        <ul className="space-y-3 bg-[#F3F2EF] px-5 py-4" data-testid={`run-${run.id}-details`}>
          {jobs.map((j) => (
            <li key={j.key} className="bg-white p-4">
              <div className="flex flex-wrap items-center gap-2"><Badge s={j.status} /><span className="text-sm font-bold">{j.label}</span><span className="text-sm text-[#3a3836]">{j.summary}</span></div>
              {Array.isArray(j.details) && j.details.length > 0 && <ul className="mt-2 space-y-0.5 text-xs text-[#57534E]">{j.details.map((d, i) => <li key={i}>{d}</li>)}</ul>}
            </li>
          ))}
          {!jobs.length && <li className="text-sm text-[#57534E]">Starting…</li>}
        </ul>
      )}
    </li>
  );
};

const Backups = ({ list }) => {
  const [busy, setBusy] = useState("");
  const download = async (b) => {
    setBusy(b.id);
    try {
      const r = await api.get(`/maintenance/backups/${b.id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `cresway-backup-${b.created_at.slice(0, 16).replace(/[:T]/g, "-")}.json.gz`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="border border-black/15 bg-white" data-testid="maintenance-backups">
      <p className="border-b border-black/10 px-5 py-3 text-sm font-bold">Backups <span className="font-normal text-[#57534E]">(latest 10 kept)</span></p>
      {!list.length ? <p className="p-6 text-center text-sm text-[#57534E]" data-testid="maintenance-backups-empty">No backups yet. One is made at every maintenance.</p> : (
        <ul>
          {list.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-3 border-b border-black/5 px-5 py-3 text-sm" data-testid={`backup-${b.id}`}>
              <span className="flex-1 font-semibold">{fmtDateTime(b.created_at)}</span>
              <span className="text-xs text-[#57534E]">{b.records} records · {kb(b.size)}</span>
              <button onClick={() => download(b)} disabled={busy === b.id} className="flex items-center gap-1.5 border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-[#0A0A0A]" data-testid={`backup-${b.id}-download`}>
                {busy === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Download
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AutoMaintenance = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/maintenance/auto").then((r) => setData(r.data && typeof r.data === "object" ? r.data : null)).catch(() => {}), []);
  const runs = Array.isArray(data?.runs) ? data.runs : [];
  const page = useSeeMore(runs);
  const running = runs.some((r) => r.status === "running");
  useEffect(() => { load(); return undefined; }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);

  const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  const save = async (patch) => {
    const next = { jobs: Object.fromEntries(jobs.map((j) => [j.key, j.enabled])), email_report: data?.email_report ?? true, ...patch };
    setData((d) => ({ ...d, email_report: next.email_report, jobs: jobs.map((j) => ({ ...j, enabled: next.jobs[j.key] })) }));
    try { await api.put("/maintenance/auto", next); } catch (e) { toast.error(errText(e)); load(); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const r = await api.post("/maintenance/auto/run"); setOpen(r.data?.id ?? null); toast.success("Maintenance jobs started"); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const delRun = async (r) => {
    try { await api.delete(`/maintenance/auto/runs/${r.id}`); toast.success("Report deleted"); load(); } catch (e) { toast.error(errText(e)); }
  };
  const clearRuns = async () => {
    if (!window.confirm("Delete all finished maintenance reports? This can't be undone.")) return;
    try { const r = await api.delete("/maintenance/auto/runs"); toast.success(`Deleted ${r.data?.deleted ?? 0} report(s)`); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <section data-testid="maintenance-auto">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Automatic maintenance</h2>
          <p className="mt-1 max-w-2xl text-sm text-[#57534E]">These jobs run by themselves when a scheduled maintenance starts. You can also run them any time without taking the site offline.</p>
        </div>
        <button onClick={runNow} disabled={busy || running || !data} data-testid="maintenance-auto-run" className="flex items-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#E62314] disabled:opacity-50">
          {busy || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{running ? "Running…" : "Run maintenance now"}
        </button>
      </div>
      {!data ? <div className="mt-5 h-40 animate-pulse bg-black/5" /> : (
        <div className="mt-5 grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-2">
            <ul className="divide-y divide-black/5 border border-black/15 bg-white" data-testid="maintenance-auto-jobs">
              {jobs.map((j) => (
                <li key={j.key} className="flex items-start justify-between gap-4 px-5 py-3.5">
                  <div><p className="text-sm font-bold">{j.label}</p><p className="text-xs text-[#57534E]">{j.description}</p></div>
                  <Switch checked={j.enabled} onCheckedChange={(v) => save({ jobs: { ...Object.fromEntries(jobs.map((x) => [x.key, x.enabled])), [j.key]: v } })} data-testid={`auto-job-${j.key}`} />
                </li>
              ))}
              <li className="flex items-center justify-between gap-4 bg-[#F3F2EF] px-5 py-3.5">
                <div><p className="text-sm font-bold">Email me the report</p><p className="text-xs text-[#57534E]">Sent to your admin email after every run</p></div>
                <Switch checked={!!data.email_report} onCheckedChange={(v) => save({ email_report: v })} data-testid="auto-email-report" />
              </li>
            </ul>
            <Backups list={Array.isArray(data.backups) ? data.backups : []} />
          </div>
          <div className="lg:col-span-3">
            <div className="border border-black/15 bg-white" data-testid="maintenance-auto-runs">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/10 px-5 py-3">
                <p className="text-sm font-bold">Reports {runs[0] && <span className="font-normal text-[#57534E]">· last run {fmtRelative(runs[0].started_at)}</span>}</p>
                {runs.some((r) => r.status !== "running") && <button onClick={clearRuns} data-testid="runs-clear" className="flex items-center gap-1.5 text-xs font-semibold text-[#57534E] hover:text-[#E62314]"><Trash2 className="h-3.5 w-3.5" />Clear all</button>}
              </div>
              {!runs.length ? <p className="p-8 text-center text-sm text-[#57534E]" data-testid="maintenance-auto-runs-empty">No runs yet.</p> : (
                <>
                  <ul>{page.shown.map((r) => <RunRow key={r.id} run={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} onDelete={() => delRun(r)} />)}</ul>
                  <SeeMore {...page} testId="runs" />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
